import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Send, RefreshCw, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import { templateRequestsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/UI/PageHeader';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import StatusBadge from '../../components/UI/StatusBadge';

const STATUS_CFG = {
  draft:                  { label: 'Draft',              tone: 'neutral' },
  pending_admin_approval: { label: 'Pending Approval',    tone: 'yellow' },
  approved:               { label: 'Approved',            tone: 'info' },
  rejected:               { label: 'Rejected',            tone: 'danger' },
  revision_requested:     { label: 'Revision Requested',  tone: 'orange' },
  generated:              { label: 'Generated',           tone: 'teal' },
  active:                 { label: 'Active',              tone: 'success' },
  archived:               { label: 'Archived',            tone: 'neutral' },
};

const EMPTY_FORM = {
  template_name: '', use_case: '', airline: '', zone_context: '',
  wording: '', variablesText: '', languagesText: '', notes: '', example_announcement: '',
};

export default function RequestNewTemplatePage() {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await templateRequestsAPI.getAll();
      setMine(res.data.data || []);
    } catch { /* non-critical */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  function update(field, value) {
    setForm(f => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.template_name.trim() || !form.use_case.trim() || !form.wording.trim()) {
      toast.error('Template name, use case, and wording are required');
      return;
    }
    setSubmitting(true);
    try {
      await templateRequestsAPI.submit({
        template_name: form.template_name.trim(),
        use_case: form.use_case.trim(),
        airline: form.airline.trim() || undefined,
        zone_context: form.zone_context.trim() || undefined,
        wording: form.wording.trim(),
        variables: form.variablesText.split(',').map(s => s.trim()).filter(Boolean),
        languages: form.languagesText.split(',').map(s => s.trim()).filter(Boolean),
        notes: form.notes.trim() || undefined,
        example_announcement: form.example_announcement.trim() || undefined,
      });
      toast.success('Submitted — an admin will review your template request');
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      if (!err._toasted) toast.error(err.response?.data?.message || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  }

  // CR 11 — "A Manager can request a new template. A Supervisor cannot." The
  // route itself is reachable by supervisor/admin too (shared layout), so
  // enforce the distinction here; the backend rejects non-managers regardless.
  if (user && user.role !== 'manager') {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <Lock size={32} className="mx-auto text-gray-300 mb-3" />
        <p className="text-gray-500 font-medium">Manager access only</p>
        <p className="text-sm text-gray-400 mt-1">Only Managers can request a new template.</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <PageHeader
        icon={FileText}
        title="Request New Template"
        subtitle="Ask Admin to add a new announcement template to the library"
      />

      <Card as="form" onSubmit={handleSubmit} className="space-y-4 mb-8">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Template Name *</label>
          <input
            type="text" value={form.template_name}
            onChange={e => update('template_name', e.target.value)}
            className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Intended Use Case *</label>
          <textarea
            value={form.use_case} onChange={e => update('use_case', e.target.value)} rows={2}
            className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Airline (optional)</label>
            <input
              type="text" value={form.airline} onChange={e => update('airline', e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Zone / Context (optional)</label>
            <input
              type="text" value={form.zone_context} onChange={e => update('zone_context', e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Template Wording *</label>
          <textarea
            value={form.wording} onChange={e => update('wording', e.target.value)} rows={4}
            placeholder="Use {VariableName} for placeholders, e.g. Flight {FlightNumber} to {Destination}..."
            className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Required Variables (comma-separated)</label>
          <input
            type="text" value={form.variablesText} onChange={e => update('variablesText', e.target.value)}
            placeholder="FlightNumber, Destination, Gate"
            className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Required Languages (comma-separated)</label>
          <input
            type="text" value={form.languagesText} onChange={e => update('languagesText', e.target.value)}
            placeholder="en, zh-CN"
            className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Example Completed Announcement (optional)</label>
          <textarea
            value={form.example_announcement} onChange={e => update('example_announcement', e.target.value)} rows={2}
            className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes (optional)</label>
          <textarea
            value={form.notes} onChange={e => update('notes', e.target.value)} rows={2}
            className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          />
        </div>
        <Button type="submit" size="xl" icon={Send} loading={submitting} fullWidth>
          {submitting ? 'Submitting…' : 'Submit Request'}
        </Button>
      </Card>

      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-700">My Requests</h2>
        <button onClick={load} className="p-1.5 rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-50">
          <RefreshCw size={14} />
        </button>
      </div>
      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : mine.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-8">No requests submitted yet</p>
      ) : (
        <div className="space-y-2">
          {mine.map(r => {
            const st = STATUS_CFG[r.status] || STATUS_CFG.draft;
            return (
              <div key={r.id} className="bg-white rounded-xl border border-gray-200 px-4 py-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold text-gray-900">{r.template_name}</span>
                  <StatusBadge label={st.label} tone={st.tone} />
                </div>
                <p className="text-xs text-gray-500 line-clamp-1">{r.use_case}</p>
                {r.admin_notes && (
                  <p className="text-xs text-gray-400 mt-1">Admin note: {r.admin_notes}</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
